import { StylomeLargeEditLensReport } from "./StylomeLargeEditLensReport";
import { StylomeLargePanReport } from "./StylomeLargePanReport";
import { StylomeLargeStoryScopeReport } from "./StylomeLargeStoryScopeReport";
import { BenchmarkMetric } from "./BenchmarkMetric";

export function StylomeLargeTransparencyReport() {
  return (
    <>
      <section className="content-section benchmark-section">
        <StylomeLargeEditLensReport />
      </section>
      <section className="content-section benchmark-section">
        <StylomeLargePanReport />
      </section>
      <section className="content-section benchmark-section">
        <StylomeLargeStoryScopeReport />
      </section>
      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">
                Stylome 1.2 Large · ELLIPSE Corpus
              </span>
              <strong>ELLIPSE</strong>
              <span>
                Standardized-test essays written by English learners in grades
                8–12.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.2 Large ELLIPSE highlights"
            >
              <BenchmarkMetric
                label="False-positive rate · 0.01% FPR target"
                value="0%"
                context="0 of 1,713 human essays flagged"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              All 1,713 essays were classified as human at both fixed operating
              points. No essays were excluded or truncated, and the thresholds
              were fixed before scoring.
            </p>
            <p>
              This evaluation uses the established frozen held half of the{" "}
              <a
                href="https://doi.org/10.1075/ijlcr.22026.cro"
                target="_blank"
                rel="noreferrer"
              >
                ELLIPSE corpus
              </a>
              . The same 1,713 human-written essays were evaluated for earlier
              Stylome versions.
            </p>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.2 Large ELLIPSE results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.2 Large on the frozen ELLIPSE half</caption>
                <thead>
                  <tr>
                    <th>Nominal calibration FPR target</th>
                    <th>Human essays</th>
                    <th>False positives</th>
                    <th>Observed false-positive rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">0.1%</th>
                    <td>1,713</td>
                    <td>0</td>
                    <td>0%</td>
                  </tr>
                  <tr>
                    <th scope="row">0.01%</th>
                    <td>1,713</td>
                    <td>0</td>
                    <td>0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluated September 16, 2026. The exact two-sided 95% binomial
              interval is 0–0.2151%, using the independent-essay assumption.
            </p>
          </div>
        </details>
      </section>
      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">
                Stylome 1.2 Large · PERSUADE 2.0
              </span>
              <strong>PERSUADE</strong>
              <span>
                Argumentative essays written by United States students in grades
                6–12.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.2 Large PERSUADE highlights"
            >
              <BenchmarkMetric
                label="False-positive rate · 0.01% FPR target"
                value="0%"
                context="0 of 893 human essays flagged"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              All 893 essays were classified as human at both fixed operating
              points. No essays were excluded or truncated, and the thresholds
              were fixed before scoring.
            </p>
            <p>
              This evaluation uses the established frozen held half of the{" "}
              <a
                href="https://doi.org/10.1016/j.asw.2024.100865"
                target="_blank"
                rel="noreferrer"
              >
                PERSUADE corpus
              </a>
              . The same 893 human-written essays were evaluated for earlier
              Stylome versions.
            </p>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.2 Large PERSUADE results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.2 Large on the frozen PERSUADE half</caption>
                <thead>
                  <tr>
                    <th>Nominal calibration FPR target</th>
                    <th>Human essays</th>
                    <th>False positives</th>
                    <th>Observed false-positive rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">0.1%</th>
                    <td>893</td>
                    <td>0</td>
                    <td>0%</td>
                  </tr>
                  <tr>
                    <th scope="row">0.01%</th>
                    <td>893</td>
                    <td>0</td>
                    <td>0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluated September 16, 2026. The exact two-sided 95% binomial
              interval is 0–0.4122%, using the independent-essay assumption.
            </p>
          </div>
        </details>
      </section>
      <section className="content-section benchmark-section">
        <details className="benchmark-report">
          <summary>
            <span className="benchmark-identity">
              <span className="metadata-line">
                Stylome 1.2 Large · Liang et al.
              </span>
              <strong>TOEFL91</strong>
              <span>
                Human essays written by people learning English as an additional
                language.
              </span>
            </span>
            <span
              className="benchmark-summary-metrics"
              aria-label="Stylome 1.2 Large TOEFL91 highlights"
            >
              <BenchmarkMetric
                label="False-positive rate · 0.01% FPR target"
                value="2.20%"
                context="2 of 91 human essays flagged"
              />
            </span>
            <span className="benchmark-toggle">Full results</span>
          </summary>
          <div className="benchmark-body">
            <p className="benchmark-context">
              Stylome 1.2 Large correctly cleared 89 of 91 human essays at both
              fixed operating points. The same two essays were flagged at each
              point. No essays were excluded or truncated, and the thresholds
              were fixed before scoring.
            </p>
            <p>
              This evaluation uses the established 91-essay panel from{" "}
              <a
                href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10382961/"
                target="_blank"
                rel="noreferrer"
              >
                the TOEFL91 study
              </a>
              . The same 91 human-written essays were evaluated for earlier
              Stylome versions.
            </p>
            <div
              className="table-wrap threshold-table"
              role="region"
              aria-label="Stylome 1.2 Large TOEFL91 results"
              tabIndex={0}
            >
              <table>
                <caption>Stylome 1.2 Large on TOEFL91</caption>
                <thead>
                  <tr>
                    <th>Nominal calibration FPR target</th>
                    <th>Human essays</th>
                    <th>False positives</th>
                    <th>Observed false-positive rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">0.1%</th>
                    <td>91</td>
                    <td>2</td>
                    <td>2.20%</td>
                  </tr>
                  <tr>
                    <th scope="row">0.01%</th>
                    <td>91</td>
                    <td>2</td>
                    <td>2.20%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="fine evaluation-note">
              Evaluated September 16, 2026. The exact two-sided 95% binomial
              interval for the false-positive rate is 0.267–7.715%, using the
              independent-essay assumption.
            </p>
          </div>
        </details>
      </section>
    </>
  );
}
